import { GOOGLE_CLIENT_ID, GOOGLE_SCOPES, GOOGLE_ENDPOINTS } from '../config/googleOAuth';

class GoogleOAuthService {
    constructor() {
        this.clientId = GOOGLE_CLIENT_ID;
        this.scopes = GOOGLE_SCOPES;
    }

    // Generate Google OAuth URL
    generateAuthUrl(redirectUri) {
        const params = new URLSearchParams({
            client_id: this.clientId,
            redirect_uri: redirectUri,
            scope: this.scopes,
            response_type: 'code',
            access_type: 'offline',
            include_granted_scopes: 'true'
        });

        return `${GOOGLE_ENDPOINTS.AUTH}?${params.toString()}`;
    }

    // Exchange authorization code for access token
    async exchangeCodeForToken(code, redirectUri) {
        try {
            const response = await fetch(GOOGLE_ENDPOINTS.TOKEN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    client_id: this.clientId,
                    client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
                    code: code,
                    grant_type: 'authorization_code',
                    redirect_uri: redirectUri,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to exchange code for token');
            }

            const data = await response.json();
            return {
                success: true,
                access_token: data.access_token,
                id_token: data.id_token,
                refresh_token: data.refresh_token
            };
        } catch (error) {
            console.error('Error exchanging code for token:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Get user info from Google
    async getUserInfo(accessToken) {
        try {
            const response = await fetch(GOOGLE_ENDPOINTS.USER_INFO, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to get user info');
            }

            const userInfo = await response.json();
            return {
                success: true,
                userInfo: {
                    id: userInfo.id,
                    email: userInfo.email,
                    name: userInfo.name,
                    picture: userInfo.picture,
                    verified_email: userInfo.verified_email
                }
            };
        } catch (error) {
            console.error('Error getting user info:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Handle Google Sign-In with Google Identity Services
    async handleGoogleSignIn(credential) {
        try {
            // Decode the JWT token to get user info
            const payload = JSON.parse(atob(credential.split('.')[1]));
            
            return {
                success: true,
                userInfo: {
                    id: payload.sub,
                    email: payload.email,
                    name: payload.name,
                    picture: payload.picture,
                    verified_email: payload.email_verified
                },
                idToken: credential
            };
        } catch (error) {
            console.error('Error handling Google sign-in:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Initialize Google Identity Services
    initializeGoogleIdentity() {
        return new Promise((resolve, reject) => {
            if (window.google) {
                window.google.accounts.id.initialize({
                    client_id: this.clientId,
                    callback: (response) => {
                        resolve(response);
                    },
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });
            } else {
                reject(new Error('Google Identity Services not loaded'));
            }
        });
    }

    // Render Google Sign-In button
    renderGoogleSignIn(elementId) {
        if (window.google && window.google.accounts) {
            window.google.accounts.id.renderButton(
                document.getElementById(elementId),
                {
                    theme: 'outline',
                    size: 'large',
                    type: 'standard',
                    text: 'signin_with',
                    shape: 'rectangular',
                    logo_alignment: 'left',
                }
            );
        }
    }

    // Prompt Google Sign-In
    promptGoogleSignIn() {
        if (window.google && window.google.accounts) {
            window.google.accounts.id.prompt();
        }
    }
}

export default new GoogleOAuthService(); 